import { IPageContent } from "api/interfaces/static-content.type";
import classnames from "classnames";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import QS from "query-string";
import useSafeRouter from "../../hoc/useSafeRouter";
import { deepFind } from "../../util/helper/deepFind";
import { encode } from "../../util/helper/encode";
import { capitalize } from "../../util/lodash/capitalize";
import CategoryViewItem from "../categories-viewer/CategoryViewItem";
import withErrorHandler from "../design-system/withErrorHandler";
import BreadcrumbItem from "./breadcrumb-item.component";

const BreadcrumbDropdownItem = dynamic(() => import(/* webpackChunkName: "breadcrumb-dropdown-item" */ "./breadcrumb-dropdown-item.component"));

const AllProducts = dynamic(() => import(/* webpackChunkName: "breadcrum-all-products" */"./AllProducts"));
const T1Breadcrum = dynamic(() => import(/* webpackChunkName: "breadcrum-t1" */"./T1Breadcrum"));
const T2Breadcrum = dynamic(() => import(/* webpackChunkName: "breadcrum-t2" */"./T2Breadcrum"));
const AllMakes = dynamic(() => import(/* webpackChunkName: "all-makes" */"component/breadcrumb/AllMakes"));
const AllManufactures = dynamic(() => import(/* webpackChunkName: "all-manufacturers" */"component/breadcrumb/AllManufactures"));
const AllModels = dynamic(() => import(/* webpackChunkName: "all-models" */"component/breadcrumb/AllModels"));
const StaticBredcrumList = dynamic(() => import(/* webpackChunkName: "static-breadcrumb-list" */"component/breadcrumb/StaticBredcrumList"));

const formatTitle = (title: any, _encode: any) => encode(title, _encode, " ");

interface IProps{
  staticContent: IPageContent;
  tier0?: string;
  tier1?: string;
  tier2?: string;
  tier3?: string;
  showAllProducts?: boolean;
  className?: string;
  breadCrumbsV2?: any[];
  bannerData?: any;
}

function BreadcrumbRegular(props: IProps) {
  const {
    className,
    breadCrumbsV2 = [],
    staticContent,
    bannerData,
    tier0: propTier0,
    tier1: propTier1 = "",
    tier2: propTier2 = "",
    tier3: propTier3 = "",
    showAllProducts = true,
  } = props;
  // todo remove staticContent, t0, t1, t2, t3 and pass category object as props.
  const { pageDetails = {} } = staticContent || {};
  const singleBredCRumList = breadCrumbsV2[0] || [];

  const { query } = useSafeRouter();
  const { t } = useTranslation(["common"]);
  const pageDisplayableTitle = deepFind(pageDetails, "pageDisplayableTitle", bannerData?.categoryDisplayableTitle);
  const { pathname } = query;

  const tier0 = propTier0 || pageDetails.tier0 || "";
  const tier1 = propTier1 || pageDetails.tier1 || "";
  const tier2 = propTier2 || pageDetails.tier2 || "";
  const tier3 = propTier3 || pageDetails.tier3;

  const firstBreadcrum = (
    <BreadcrumbDropdownItem
      id="breadcrumb_t0"
      className="tier0"
      title={t("Home")}
    >

      <AllProducts />
    </BreadcrumbDropdownItem>
  );

  let currentPageBreadcrum;
  let filtersBreadCrums = [];
  let finalLastBreadcrumsUI: any = [];
  if (pageDisplayableTitle) {
    currentPageBreadcrum = { title: pageDisplayableTitle, url: `/${pathname}` };
    filtersBreadCrums = [query.tier1, query.tier2, query.tier3]
      .filter(Boolean)
      .map((_) => ({
        title: capitalize(_),
        url: `/${pathname}?${QS.stringify({
          tier1: query.tier1,
          tier2: query.tier2,
          // we don't add T3 since, remove URL from last item
          // tier3: index <= 2 ? query.tier3 : undefined,
        })}`,
      }));
    const finalLastBreadcrums = [currentPageBreadcrum, ...filtersBreadCrums];
    finalLastBreadcrumsUI = finalLastBreadcrums
      .map((item, index) => (
        <BreadcrumbItem
          key={`${item.title} ${index}`}
          {...item}
          // remove url from last BreadcrumLi
          url={index === finalLastBreadcrums.length - 1 ? null : item.url}
        />
      ));
  }

  const mainBreadcrumList = singleBredCRumList.map((_: any, index: any) => {
    const { pageType, title, url } = _;
    const formatedTitle = formatTitle(title, "(-|_)");

    const TierCategoryView = ({
      isTier3,
    }: any) => {
      const categoryObj = { tier0, tier1, tier2 };
      if (isTier3) {
        categoryObj.tier3 = tier3;
      }
      return (

        <CategoryViewItem
          url={url}
          passOnClick
          categoryObj={categoryObj}
          defaultFilters={{
            manufacturers: [],
            prices: [],
            fitments: [],
            availabilities: [],
          }}
          key={`${pageType} ${index}`}
        >

          <BreadcrumbItem
            title={formatedTitle}
          />
        </CategoryViewItem>
      );
    };
    switch (pageType) {
    // https://partsavatar.ca/hyundai-front-strut
    case "TIER0_PAGE":
      return (
        <BreadcrumbDropdownItem
          id="breadcrumb_t1"
          className="tier1"
          title={formatedTitle}
          key={`${pageType} ${index}`}
        >

          <T1Breadcrum {...{ tier0 }} />
        </BreadcrumbDropdownItem>
      );
    case "TIER1_PAGE":
      return (
        <BreadcrumbDropdownItem
          id="breadcrumb_t2"
          className="tier2"
          title={formatedTitle}
          key={`${pageType} ${index}`}
        >

          <T2Breadcrum {...{ tier0, tier1 }} />
        </BreadcrumbDropdownItem>
      );
    case "TIER2_PAGE":
    case "TIER2_MAKE_PAGE": // ford-focus-exhaust-mufflers
    case "MAKE_MODEL_PAGE":
    case "TIER2_MAKE_MODEL_PAGE": // ?
      return (
        <TierCategoryView />
      );

    case "TIER3_PAGE":
    case "TIER3_MAKE_PAGE": // ford-focus-rear-brake-shoe
    case "TIER3_MAKE_MODEL_PAGE": // 1995-ford-ranger-oil-filter-transit-warehouse-lop-lp3600
      return (<TierCategoryView isTier3 />);

    case "ALL_MANUFACTURERS_PAGE":
      return (
        <BreadcrumbDropdownItem
          id="breadcrumb_t0"
          className="tier0"
          title={formatedTitle}
          key={`${pageType} ${index}`}
        >

          <AllManufactures makeUrl={url} />
        </BreadcrumbDropdownItem>
      );
    case "ALL_MAKES_PAGE":
      return (
        <BreadcrumbDropdownItem
          id="breadcrumb_t0"
          className="tier0"
          title={t("All Makes")}
        >

          <AllMakes />
        </BreadcrumbDropdownItem>
      );
    case "MAKE_PAGE":
      return (
        <BreadcrumbDropdownItem
          id="breadcrumb_t0"
          className="tier0"
          title={formatedTitle}
          key={`${pageType} ${index}`}
        >

          <AllModels makeUrl={url} />
        </BreadcrumbDropdownItem>
      );
    case "STATIC_PAGE":
    case "MANUFACTURER_PAGE":
    case "LANDING_PAGE":
      return (
        <BreadcrumbItem
          title={formatedTitle}
          url={url}
        />
      );
    case "ARTICLES":
      return (
        <BreadcrumbDropdownItem
          id="breadcrumb_t2"
          className="tier2"
          title={formatedTitle}
          key={`${pageType} ${index}`}
        >
          <StaticBredcrumList />
        </BreadcrumbDropdownItem>
      );
    default:
      return formatedTitle;
    }
  });

  return (
    <ul
      className={classnames("breadcrumb", "breadcrumb-category", className)}
    >
      {firstBreadcrum}
      {mainBreadcrumList}
      {finalLastBreadcrumsUI}
    </ul>
  );
}

export default withErrorHandler(BreadcrumbRegular);
